<template>
  <div>
    <v-container class="chat-container" fluid>
      <!-- Chat Header -->
      <v-row>
        <v-col cols="12">
          <v-card class="chat-header-card" elevation="3">
            <v-card-title class="d-flex align-center justify-space-between pa-4">
              <div class="d-flex align-center">
                <v-avatar color="primary" class="me-3">
                  <v-icon color="white">mdi-chat</v-icon>
                </v-avatar>
                <div>
                  <div class="d-flex align-center">
                    <h2 class="text-h5 font-weight-bold mb-1">Live Chat</h2>
                    <v-chip 
                      :color="connectionStatus === 'connected' ? 'success' : connectionStatus === 'connecting' ? 'warning' : 'error'" 
                      variant="flat" 
                      size="small"
                      class="ml-2"
                    >
                      <v-icon class="me-1" size="12">
                        {{ connectionStatus === 'connecting' ? 'mdi-loading mdi-spin' : connectionStatus === 'connected' ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      {{ connectionStatus === 'connected' ? 'Live' : connectionStatus === 'connecting' ? 'Connecting' : 'Offline' }}
                    </v-chip>
                  </div>
                </div>
              </div>
              <div class="d-flex align-center gap-2">
                <!-- <v-btn 
                  @click="broadcastMessage"
                  color="primary" 
                  variant="elevated"
                  size="small"
                  :loading="broadcastLoading"
                >
                  <v-icon class="me-1">mdi-bullhorn</v-icon>
                  Broadcast
                </v-btn>
                <v-btn 
                  @click="forceSyncUserStatuses"
                  color="info" 
                  variant="outlined"
                  size="small"
                  title="Force sync user statuses"
                >
                  <v-icon>mdi-account-sync</v-icon>
                </v-btn> -->
                <!-- <v-chip :color="chatStatus === 'enabled' ? 'success' : 'error'" variant="flat" size="small">
                  <v-icon class="me-1">{{ chatStatus === 'enabled' ? 'mdi-circle' : 'mdi-circle-outline' }}</v-icon>
                  {{ chatStatus === 'enabled' ? 'Online' : 'Offline' }}
                </v-chip> -->
              </div>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Socket.IO Connection Section - Only show if disconnected and not auto-connecting -->
      <v-row class="mt-2" v-if="connectionStatus === 'disconnected' && !isAutoConnecting">
        <v-col cols="12">
          <v-card class="auth-card" elevation="2">
            <v-card-title class="d-flex align-center pa-3">
              <v-icon class="me-2" color="warning">mdi-connection</v-icon>
              <span class="text-h6">Connection Lost</span>
              <v-spacer></v-spacer>
              <v-chip color="error" variant="flat" size="small">
                <v-icon class="me-1">mdi-circle-outline</v-icon>
                Disconnected
              </v-chip>
            </v-card-title>
            <v-card-text class="pa-3">
              <v-alert type="warning" variant="tonal" class="mb-3">
                <strong>Connection to Seham.tv chat lost!</strong><br>
                Click reconnect to restore live chat functionality as Admin (Moderator).
              </v-alert>
              <v-row>
                <v-col cols="12">
                  <v-btn
                    @click="reconnectToChat"
                    color="primary"
                    variant="elevated"
                    :loading="connectionStatus === 'connecting'"
                    size="large"
                    block
                  >
                    <v-icon class="me-2">mdi-connection</v-icon>
                    Reconnect to Chat as Admin
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Auto-connecting Status -->
      <v-row class="mt-2" v-if="connectionStatus === 'connecting' && isAutoConnecting">
        <v-col cols="12">
          <v-card class="auth-card" elevation="2">
            <v-card-text class="pa-4 text-center">
              <v-progress-circular
                indeterminate
                color="primary"
                size="32"
                class="mb-3"
              ></v-progress-circular>
              <h3 class="text-h6 mb-2">Connecting to Seham.tv Live Chat...</h3>
              <p class="text-body-2 text-grey">
                Establishing connection with {{ selectedClient?.name || 'Seham.tv' }}
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Chat Interface -->
      <v-row class="mt-4">
        <!-- Chat Messages Panel -->
        <v-col cols="12" lg="8">
          <v-card class="chat-messages-card" elevation="2">
            <v-card-title class="chat-title d-flex align-center justify-space-between pa-3">
              <div class="d-flex align-center">
                <v-icon class="me-2" color="primary">mdi-message-text</v-icon>
                <span class="text-h6">Messages</span>
              </div>
              <div class="d-flex align-center">
                <!-- <v-btn 
                  icon
                  size="small"
                  @click="toggleAutoScroll"
                  :color="autoScroll ? 'grey' : 'white'"
                  variant="tonal"
                >
                  <v-icon>{{ autoScroll ? 'mdi-arrow-down-bold-circle' : 'mdi-arrow-down-bold-circle-outline' }}</v-icon>
                </v-btn> -->
                <v-btn 
                  icon
                  size="small"
                  @click="clearAllChats"
                  color="error"
                  variant="tonal"
                  class="ml-2"
                  :loading="clearLoading"
                >
                  <v-icon>mdi-delete-sweep</v-icon>
                </v-btn>
              </div>
            </v-card-title>
            
            <!-- Messages Container -->
            <div class="chat-messages-wrapper">
              <div ref="chatContainer" class="chat-messages-container" @scroll="handleScroll">
                <div 
                  v-for="message in messages" 
                  :key="message._id"
                  class="message-wrapper"
                  :class="{ 'flagged-message': message.flagged }"
                >
                  <v-card 
                    class="message-card" 
                    :class="{ 'system-message': message.isSystem, 'broadcast-message': message.isBroadcast }"
                    elevation="1"
                    variant="outlined"
                  >
                    <v-card-text class="pa-3">
                      <div class="d-flex align-center justify-space-between mb-2">
                        <div class="d-flex align-center">
                          <v-avatar 
                            size="32" 
                            class="me-2" 
                            :color="message.isSystem ? 'grey' : (message.avatar ? 'transparent' : getUserAvatarColor(message.userType))"
                          >
                            <v-img 
                              v-if="message.avatar && !message.isSystem"
                              :src="message.avatar"
                              :alt="message.username"
                              cover
                            >
                              <template v-slot:error>
                                <div class="d-flex align-center justify-center fill-height">
                                  <v-icon size="16" color="white">mdi-account</v-icon>
                                </div>
                              </template>
                            </v-img>
                            <v-icon v-else size="16" color="white">
                              {{ message.isSystem ? 'mdi-cog' : 'mdi-account' }}
                            </v-icon>
                          </v-avatar>
                          <div>
                          <div class="username text-body-2 font-weight-medium d-flex align-center">
                            {{ message.username }}
                          </div>
                          <div class="timestamp text-caption text-grey">{{ formatDateTime(message.timestamp) }}</div>
                          </div>
                        </div>
                        <div class="d-flex align-center">
                          <v-chip 
                            v-if="message.flagged" 
                            color="error" 
                            size="x-small" 
                            class="me-2"
                            variant="flat"
                          >
                            FLAGGED
                          </v-chip>
                          <v-menu v-if="!message.isSystem">
                            <template v-slot:activator="{ props }">
                              <v-btn icon size="x-small" variant="text" v-bind="props">
                                <v-icon size="16">mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>
                            <v-list density="compact">
                              <v-list-item @click="deleteMessage(message)" prepend-icon="mdi-delete">
                                <v-list-item-title>Delete Message</v-list-item-title>
                              </v-list-item>
                              <!-- <v-list-item @click="flagMessage(message)" :prepend-icon="message.flagged ? 'mdi-flag-remove' : 'mdi-flag'">
                                <v-list-item-title>{{ message.flagged ? 'Unflag' : 'Flag' }} Message</v-list-item-title>
                              </v-list-item>
                              <v-list-item @click="showUserActions(message.userId)" prepend-icon="mdi-account-cog">
                                <v-list-item-title>User Actions</v-list-item-title>
                              </v-list-item> -->
                            </v-list>
                          </v-menu>
                        </div>
                      </div>
                      <div class="message-content text-body-1">{{ message.content }}</div>
                    </v-card-text>
                  </v-card>
                </div>
                <div v-if="messages.length === 0" class="empty-state text-center pa-8">
                  <v-icon size="64" color="grey-lighten-2">mdi-chat-outline</v-icon>
                  <p class="text-grey mt-3 text-h6">No messages yet</p>
                  <p class="text-grey text-body-2">Start a conversation!</p>
                </div>
              </div>
              
              <!-- Typing Indicator -->
              <div v-if="typingUsers.length > 0" class="typing-indicator-container pa-2">
                <div class="typing-indicator">
                  <div class="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span class="typing-text">{{ typingUsers.join(', ') }} {{ typingUsers.length === 1 ? 'is' : 'are' }} typing...</span>
                </div>
              </div>
            </div>
            
            <!-- Message Input Section -->
            <v-card-actions class="message-input-section pa-0">
              <div class="message-input-container">
                <div v-if="connectionStatus !== 'connected'" class="connection-warning">
                  <v-alert 
                    type="warning" 
                    variant="tonal" 
                    density="compact"
                    class="ma-3 mb-0"
                  >
                    <template v-slot:prepend>
                      <v-icon>mdi-wifi-off</v-icon>
                    </template>
                    <span class="text-caption">Connect to chat to send messages</span>
                  </v-alert>
                </div>
                
                <div class="input-wrapper">
                  <div class="input-container">
                    <v-text-field
                      v-model="newMessage"
                      placeholder="Type your message..."
                      variant="solo"
                      density="comfortable"
                      hide-details
                      :disabled="connectionStatus !== 'connected'"
                      @keyup.enter="sendSocketMessage"
                      @input="handleTyping"
                      class="message-input"
                      rounded
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey-darken-1" size="20">mdi-message-text-outline</v-icon>
                      </template>
                      <template v-slot:append-inner>
                        <v-btn
                          @click="sendSocketMessage"
                          :disabled="!newMessage.trim() || connectionStatus !== 'connected'"
                          icon
                          variant="flat"
                          color="primary"
                          size="small"
                          class="send-button"
                        >
                          <v-icon size="18">mdi-send</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                </div>
              </div>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- Users Panel -->
        <v-col cols="12" lg="4">
          <v-card class="users-panel-card" elevation="2">
            <v-card-title class="users-title d-flex align-center justify-space-between pa-3">
              <div class="d-flex align-center">
                <v-icon class="me-2" color="primary">mdi-account-group</v-icon>
                <span class="text-h6">Users</span>
                <v-chip class="ml-2" color="primary" size="small" variant="tonal">
                  {{ data.length }}
                </v-chip>
                <v-chip class="ml-1" color="success" size="small" variant="tonal">
                  {{ onlineUsersCount }} online
                </v-chip>
              </div>
              <div class="d-flex align-center">
                <v-btn 
                  icon
                  size="small"
                  @click="refreshLiveChatUsers"
                  color="primary"
                  variant="tonal"
                  title="Refresh live chat users"
                >
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </div>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-0">
              <v-list class="users-list" density="compact" style="padding: 0;">
                <v-list-item 
                  v-for="user in filteredUsers" 
                  :key="user._id" 
                  class="user-list-item"
                >
                  <template v-slot:prepend>
                    <v-avatar 
                      size="36" 
                      :color="user.avatar ? 'transparent' : (user.status === 'online' ? 'success' : 'grey')"
                    >
                      <v-img 
                        v-if="user.avatar"
                        :src="user.avatar"
                        :alt="user.username"
                        cover
                      >
                        <template v-slot:error>
                          <div class="d-flex align-center justify-center fill-height">
                            <v-icon color="white" size="20">mdi-account</v-icon>
                          </div>
                        </template>
                      </v-img>
                      <v-icon v-else color="white" size="20">mdi-account</v-icon>
                    </v-avatar>
                  </template>
                  <v-list-item-title class="font-weight-medium d-flex align-center">
                    {{ user.username }}
                    <v-icon 
                      v-if="user.isVerified" 
                      color="blue" 
                      size="16" 
                      class="ml-1"
                    >
                      mdi-check-circle
                    </v-icon>
                  </v-list-item-title>
                  <v-list-item-subtitle class="d-flex flex-wrap gap-1 mt-1">
                    <v-chip 
                      :color="user.status === 'online' ? 'success' : 'grey'" 
                      size="x-small" 
                      variant="flat"
                    >
                      {{ user.status }}
                    </v-chip>
                    <v-chip 
                      v-if="user.isMuted" 
                      color="warning" 
                      size="x-small"
                      variant="flat"
                    >
                      MUTED
                    </v-chip>
                    <v-chip 
                      v-if="user.isBanned" 
                      color="error" 
                      size="x-small"
                      variant="flat"
                      :title="getBanTooltip(user)"
                    >
                      {{ getBanLabel(user) }}
                    </v-chip>
                  </v-list-item-subtitle>
                  <template v-slot:append>
                    <v-menu>
                      <template v-slot:activator="{ props }">
                        <v-btn icon size="small" variant="text" v-bind="props">
                          <v-icon size="16">mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list density="compact">
                        <v-list-item @click="showBanDialog(user)" prepend-icon="mdi-cancel">
                          <v-list-item-title>{{ user.isBanned ? 'Unban' : 'Ban' }} User</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="viewUserProfile(user)" prepend-icon="mdi-account-details">
                          <v-list-item-title>View Profile</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </template>
                </v-list-item>
                <div v-if="filteredUsers.length === 0" class="empty-users text-center pa-6">
                  <v-icon size="48" color="grey-lighten-2">mdi-account-search</v-icon>
                  <p class="text-grey mt-2">No users found</p>
                </div>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- IP Ban Management Section -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card class="ip-ban-card" elevation="2">
            <v-card-title class="d-flex align-center justify-space-between pa-3">
              <div class="d-flex align-center">
                <v-icon class="me-2" color="primary">mdi-ip-network</v-icon>
                <span class="text-h6">IP Ban Management</span>
                <v-chip class="ml-2" color="primary" size="small" variant="tonal">
                  {{ bannedIps.length }} banned
                </v-chip>
              </div>
              <div class="d-flex align-center gap-2">
                <v-btn 
                  @click="showIpBanDialog = true"
                  color="primary"
                  variant="elevated"
                  size="small"
                >
                  <v-icon class="me-1">mdi-plus</v-icon>
                  Ban IP
                </v-btn>
                <v-btn 
                  icon
                  size="small"
                  @click="refreshBannedIps"
                  color="primary"
                  variant="tonal"
                  title="Refresh banned IPs"
                >
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </div>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-0">
              <v-text-field
                v-model="ipSearch"
                variant="solo-filled"
                prepend-inner-icon="mdi-magnify"
                label="Search IP addresses..."
                density="compact"
                flat
                hide-details
                class="search-field"
              ></v-text-field>
              
              <v-list class="ip-list" density="compact" max-height="300">
                <div v-for="bannedIp in filteredBannedIps" :key="bannedIp.id">
                  <v-list-item class="ip-list-item">
                    <template v-slot:prepend>
                      <v-avatar size="36" color="primary">
                        <v-icon color="white" size="20">mdi-ip</v-icon>
                      </v-avatar>
                    </template>
                    <v-list-item-title class="font-weight-medium">
                      {{ bannedIp.ipAddress }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="d-flex flex-wrap gap-1 mt-1">
                      <v-chip 
                        color="error" 
                        size="x-small" 
                        variant="flat"
                      >
                        BANNED
                      </v-chip>
                      <v-chip 
                        v-if="bannedIp.banType === 'temporary'" 
                        color="warning" 
                        size="x-small"
                        variant="flat"
                        :title="getIpBanTooltip(bannedIp)"
                      >
                        {{ getIpBanLabel(bannedIp) }}
                      </v-chip>
                      <v-chip 
                        v-else
                        color="error" 
                        size="x-small"
                        variant="flat"
                      >
                        PERMANENT
                      </v-chip>
                    </v-list-item-subtitle>
                    <template v-slot:append>
                      <div class="d-flex align-center gap-1">
                        <v-btn 
                          @click="unbanIp(bannedIp)"
                          color="success"
                          variant="tonal"
                          size="small"
                          :loading="unbanIpLoading === bannedIp.id"
                        >
                          <v-icon size="16">mdi-check</v-icon>
                          Unban
                        </v-btn>
                        <v-menu>
                          <template v-slot:activator="{ props }">
                            <v-btn icon size="small" variant="text" v-bind="props">
                              <v-icon size="16">mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <v-list density="compact">
                            <v-list-item @click="viewIpBanDetails(bannedIp)" prepend-icon="mdi-information">
                              <v-list-item-title>View Details</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="editIpBan(bannedIp)" prepend-icon="mdi-pencil">
                              <v-list-item-title>Edit Ban</v-list-item-title>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item @click="unbanIp(bannedIp)" prepend-icon="mdi-delete" class="text-error">
                              <v-list-item-title>Remove Ban</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </template>
                  </v-list-item>
                  <v-divider class="mx-4"></v-divider>
                </div>
                <div v-if="filteredBannedIps.length === 0" class="empty-ips text-center pa-6">
                  <v-icon size="48" color="grey-lighten-2">mdi-ip-network-outline</v-icon>
                  <p class="text-grey mt-2">No banned IP addresses</p>
                </div>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Broadcast Message Dialog -->
    <v-dialog v-model="broadcastDialog" width="600" persistent>
      <v-card color="primary" rounded="lg" class="pa-2" style="font-family: Saira Condensed, sans-serif;">
        <v-card-title class="text-white">
          <v-icon class="me-2">mdi-bullhorn</v-icon>
          Broadcast Message to All Users
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="broadcastText"
            label="Message"
            variant="outlined"
            rows="4"
            counter="500"
            :rules="[v => v.length <= 500 || 'Message must be less than 500 characters']"
          ></v-textarea>
          <v-select
            v-model="broadcastType"
            :items="broadcastTypes"
            label="Message Type"
            variant="outlined"
          ></v-select>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Ban Dialog -->
    <v-dialog v-model="banDialog" width="500" persistent>
        <v-card color="primary" rounded="lg" class="pa-2" style="font-family: Saira Condensed, sans-serif;">
            <v-card-title class="text-white">
              <v-icon class="me-2">mdi-cancel</v-icon>
              {{ itemToBan?.isBanned ? 'Unban' : 'Ban' }} User
            </v-card-title>
            <v-card-text class="font-weight-bold text-white">
                <div v-if="itemToBan?.isBanned">
                  Are you sure you want to unban this user?
                  <br><br><strong>{{ itemToBan?.username }}</strong>
                  <v-alert type="info" variant="tonal" class="mt-4">
                    <strong>Current Ban Info:</strong><br>
                    Type: {{ itemToBan?.banType || 'N/A' }}<br>
                    <span v-if="itemToBan?.banExpiresAt">
                      Expires: {{ formatDateTime(itemToBan.banExpiresAt) }}
                    </span>
                    <span v-else>Permanent ban</span>
                  </v-alert>
                </div>
                <div v-else>
                  Are you sure you want to ban this user?
                  <br><br><strong>{{ itemToBan?.username }}</strong>
                  
                  <!-- Ban Type Selection -->
                  <v-select
                    v-model="banType"
                    :items="banTypes"
                    label="Ban Type"
                    variant="outlined"
                    class="mt-4"
                    item-title="title"
                    item-value="value"
                  ></v-select>
                  
                  <!-- Duration Selection (only for temporary bans) -->
                  <v-select
                    v-if="banType === 'temporary'"
                    v-model="banDuration"
                    :items="banDurations"
                    label="Ban Duration"
                    variant="outlined"
                    class="mt-2"
                    item-title="title"
                    item-value="value"
                  ></v-select>
                  
                  <!-- Reason Input -->
                  <v-text-field
                    v-model="banReason"
                    label="Reason (optional)"
                    variant="outlined"
                    class="mt-2"
                  ></v-text-field>
                </div>
            </v-card-text>
            <template v-slot:actions>
                <v-spacer></v-spacer>
                <v-btn :disabled="banLoading" @click="banDialog = false" variant="outlined">
                    Cancel
                </v-btn>
                <v-btn 
                  @click="banUser()" 
                  :loading="banLoading" 
                  :color="itemToBan?.isBanned ? 'success' : 'red'" 
                  variant="elevated"
                >
                    {{ itemToBan?.isBanned ? 'Unban' : 'Ban' }} User
                </v-btn>
            </template>
        </v-card>
    </v-dialog>

    <!-- User Profile Dialog -->
    <v-dialog v-model="profileDialog" width="800">
      <v-card>
        <v-card-title>
          <v-icon class="me-2">mdi-account-details</v-icon>
          User Profile: {{ selectedUserProfile?.username }}
        </v-card-title>
        <v-card-text v-if="selectedUserProfile">
          <v-row>
            <v-col cols="12" md="6">
              <v-list>
                <v-list-item>
                  <v-list-item-title>Username</v-list-item-title>
                  <v-list-item-subtitle>{{ selectedUserProfile.username }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Status</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip :color="selectedUserProfile.status === 'online' ? 'green' : 'grey'" size="small">
                      {{ selectedUserProfile.status }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>
                    <v-icon class="me-1" size="16">mdi-check-circle</v-icon>
                    Verification Status
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip :color="selectedUserProfile.isVerified ? 'green' : 'red'" size="small">
                      {{ selectedUserProfile.isVerified ? 'Yes' : 'No' }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Join Date</v-list-item-title>
                  <v-list-item-subtitle>{{ formatDateTime(selectedUserProfile.createdAt) }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="6">
              <v-list>
                <!-- <v-list-item>
                  <v-list-item-title>Messages Sent</v-list-item-title>
                  <v-list-item-subtitle>{{ getUserMessageCount(selectedUserProfile._id) }}</v-list-item-subtitle>
                </v-list-item> -->
                <!-- <v-list-item>
                  <v-list-item-title>Flagged Messages</v-list-item-title>
                  <v-list-item-subtitle>{{ getUserFlaggedCount(selectedUserProfile._id) }}</v-list-item-subtitle>
                </v-list-item> -->
                <v-list-item>
                  <v-list-item-title>Ban Status</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip :color="selectedUserProfile.isBanned ? 'red' : 'green'" size="small">
                      {{ selectedUserProfile.isBanned ? 'Banned' : 'Active' }}
                    </v-chip>
                    <div v-if="selectedUserProfile.isBanned" class="mt-2">
                      <div class="text-caption">
                        <strong>Type:</strong> {{ selectedUserProfile.banType || 'permanent' }}
                      </div>
                      <div v-if="selectedUserProfile.banExpiresAt" class="text-caption">
                        <strong>Expires:</strong> {{ formatDateTime(selectedUserProfile.banExpiresAt) }}
                      </div>
                      <div v-if="selectedUserProfile.banCount" class="text-caption">
                        <strong>Ban Count:</strong> {{ selectedUserProfile.banCount }}
                      </div>
                    </div>
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
        <template v-slot:actions>
          <v-spacer></v-spacer>
          <v-btn @click="profileDialog = false" variant="outlined">
            Close
          </v-btn>
        </template>
      </v-card>
    </v-dialog>

    <!-- Clear Chat Confirmation Dialog -->
    <v-dialog v-model="clearDialog" width="450" persistent>
      <v-card color="warning" rounded="lg" class="pa-2">
        <v-card-title class="text-white">
          <v-icon class="me-2">mdi-delete-sweep</v-icon>
          Clear All Chat Messages
        </v-card-title>
        <v-card-text class="font-weight-bold text-white">
          This action will permanently delete ALL chat messages. This cannot be undone.
          <br><br>Are you sure you want to continue?
        </v-card-text>
        <template v-slot:actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="clearLoading" @click="clearDialog = false" variant="outlined">
            Cancel
          </v-btn>
          <v-btn @click="confirmClearChats()" :loading="clearLoading" color="red" variant="elevated">
            Clear All Messages
          </v-btn>
        </template>
      </v-card>
    </v-dialog>

    <!-- IP Ban Dialog -->
    <v-dialog v-model="showIpBanDialog" width="500" persistent>
      <v-card color="black" rounded="lg" class="pa-2" style="font-family: Saira Condensed, sans-serif;">
        <v-card-title class="text-white">
          <v-icon class="me-2">mdi-ip-network</v-icon>
          Ban IP Address
        </v-card-title>
        <v-card-text class="font-weight-bold text-white">
          <p class="mb-4">Enter the IP address you want to ban from the chat:</p>
          
          <v-text-field
            v-model="ipToBan"
            label="IP Address"
            variant="outlined"
            placeholder="192.168.1.100"
            :rules="ipValidationRules"
            class="mt-2"
          >
            <template v-slot:append-inner>
              <v-btn
                @click="checkIpBanStatus(ipToBan)"
                :disabled="!ipToBan || !isValidIp(ipToBan)"
                color="info"
                variant="text"
                size="small"
                title="Check IP ban status"
              >
                <v-icon size="16">mdi-magnify</v-icon>
                Check
              </v-btn>
            </template>
          </v-text-field>
          
          <!-- Ban Type Selection -->
          <v-select
            v-model="ipBanType"
            :items="banTypes"
            label="Ban Type"
            variant="outlined"
            class="mt-2"
            item-title="title"
            item-value="value"
          ></v-select>
          
          <!-- Duration Selection (only for temporary bans) -->
          <v-select
            v-if="ipBanType === 'temporary'"
            v-model="ipBanDuration"
            :items="banDurations"
            label="Ban Duration"
            variant="outlined"
            class="mt-2"
            item-title="title"
            item-value="value"
          ></v-select>
          
          <!-- Reason -->
          <v-textarea
            v-model="ipBanReason"
            label="Reason (Optional)"
            variant="outlined"
            rows="3"
            counter="200"
            class="mt-2"
            :rules="[v => !v || v.length <= 200 || 'Reason must be less than 200 characters']"
          ></v-textarea>
        </v-card-text>
        <template v-slot:actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="ipBanLoading" @click="closeIpBanDialog" variant="outlined">
            Cancel
          </v-btn>
          <v-btn 
            @click="banIpAddress()" 
            :loading="ipBanLoading" 
            color="primary" 
            variant="elevated"
            :disabled="!ipToBan || !isValidIp(ipToBan)"
          >
            Ban IP
          </v-btn>
        </template>
      </v-card>
    </v-dialog>

    <!-- IP Ban Details Dialog -->
    <v-dialog v-model="showIpBanDetailsDialog" width="600">
      <v-card color="primary" rounded="lg" class="pa-2" style="font-family: Saira Condensed, sans-serif;">
        <v-card-title class="text-white">
          <v-icon class="me-2">mdi-information</v-icon>
          IP Ban Details
        </v-card-title>
        <v-card-text class="text-white">
          <div v-if="selectedIpBan">
            <v-row>
              <v-col cols="12">
                <v-alert type="info" variant="tonal" class="mb-4">
                  <strong>{{ selectedIpBan.ipAddress }}</strong> is currently banned
                </v-alert>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="6">
                <div class="info-item mb-3">
                  <div class="text-caption text-grey-lighten-1">Ban Type</div>
                  <div class="font-weight-bold">{{ selectedIpBan.banType === 'permanent' ? 'Permanent' : 'Temporary' }}</div>
                </div>
              </v-col>
              <v-col cols="6" v-if="selectedIpBan.banType === 'temporary'">
                <div class="info-item mb-3">
                  <div class="text-caption text-grey-lighten-1">Expires</div>
                  <div class="font-weight-bold">{{ formatDateTime(selectedIpBan.expiresAt) }}</div>
                </div>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="6">
                <div class="info-item mb-3">
                  <div class="text-caption text-grey-lighten-1">Banned On</div>
                  <div class="font-weight-bold">{{ formatDateTime(selectedIpBan.createdAt) }}</div>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="info-item mb-3">
                  <div class="text-caption text-grey-lighten-1">Banned By</div>
                  <div class="font-weight-bold">{{ selectedIpBan.bannedBy || 'System' }}</div>
                </div>
              </v-col>
            </v-row>
            
            <v-row v-if="selectedIpBan.reason">
              <v-col cols="12">
                <div class="info-item mb-3">
                  <div class="text-caption text-grey-lighten-1">Reason</div>
                  <div class="font-weight-bold">{{ selectedIpBan.reason }}</div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <template v-slot:actions>
          <v-spacer></v-spacer>
          <v-btn @click="showIpBanDetailsDialog = false" variant="outlined">
            Close
          </v-btn>
          <v-btn 
            @click="unbanIp(selectedIpBan)"
            color="success" 
            variant="elevated"
            :loading="unbanIpLoading === selectedIpBan?.id"
          >
            Unban IP
          </v-btn>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { defineComponent } from 'vue';
import mixins from '@/mixins';
import io from 'socket.io-client';

export default defineComponent({
    mixins: [mixins],
    data(){
        return {
            // Socket.IO connection
            socket: null,
            currentUser: null,
            selectedClient: null,
            
            // Connection status
            connectionStatus: 'disconnected', // 'connecting', 'connected', 'disconnected'
            onlineCount: 0,
            isAutoConnecting: true,
            onlineUsers: [], // Track online users from socket
            
            // Client configuration for Seham.tv
            clientConfig: {
                clientId: process.env.VUE_APP_CHAT_CLIENT_ID,
                name: process.env.VUE_APP_CHAT_CLIENT_NAME,
                domain: process.env.VUE_APP_CHAT_CLIENT_DOMAIN,
                apiKey: process.env.VUE_APP_CHAT_CLIENT_API_KEY,
                roomName: process.env.VUE_APP_CHAT_ROOM_NAME,
                description: process.env.VUE_APP_CHAT_DESCRIPTION
            },
            
            // Authentication
            username: 'seham',
            userType: 'moderator',
            
            // Typing indicators
            typingTimer: null,
            isTyping: false,
            typingUsers: [],
            
            // Chat messages data
            messages: [],
            newMessage: '',
            
            // User management data
            data: [],
            user: [], // Additional user data property for live chat users
            userSearch: '',
            selectedUserProfile: null,
            
            // Chat moderation states
            chatStatus: 'enabled',
            autoScroll: true,
            
            // Dialog states
            banDialog: false,
            itemToBan: null,
            banLoading: false,
            banReason: '',
            banType: 'permanent',
            banDuration: 3600, // 1 hour in seconds

            profileDialog: false,
            broadcastDialog: false,
            broadcastLoading: false,
            broadcastText: '',
            broadcastType: 'announcement',
            clearDialog: false,
            clearLoading: false,
            
            // Loading states
            loading: false,
            liveChatUsersLoading: false,
            
            // Options
            banTypes: [
                { title: 'Permanent Ban', value: 'permanent' },
                { title: 'Temporary Ban', value: 'temporary' }
            ],
            banDurations: [
                { title: '1 Hour', value: 3600 },
                { title: '6 Hours', value: 21600 },
                { title: '12 Hours', value: 43200 },
                { title: '1 Day', value: 86400 },
                { title: '3 Days', value: 259200 },
                { title: '1 Week', value: 604800 },
                { title: '1 Month', value: 2592000 },
                { title: '3 Months', value: 7776000 }
            ],

            broadcastTypes: [
                { title: 'Announcement', value: 'announcement' },
                { title: 'Warning', value: 'warning' },
                { title: 'Info', value: 'info' },
                { title: 'Emergency', value: 'emergency' }
            ],
            
            // IP Ban Management
            bannedIps: [],
            ipSearch: '',
            showIpBanDialog: false,
            showIpBanDetailsDialog: false,
            selectedIpBan: null,
            ipToBan: '',
            ipBanType: 'permanent',
            ipBanDuration: 3600,
            ipBanReason: '',
            ipBanLoading: false,
            unbanIpLoading: null,
            
            // IP validation rules
            ipValidationRules: [
                v => !!v || 'IP address is required',
                v => this.isValidIp(v) || 'Please enter a valid IP address'
            ],
            
            // Refresh interval
            refreshInterval: null,
        }
    },
    computed: {
        filteredUsers() {
            let users = this.data;
            
            // Filter by search if there's a search term
            if (this.userSearch) {
                users = users.filter(user => 
                    user.username.toLowerCase().includes(this.userSearch.toLowerCase())
                );
            }
            
            // Sort users: online users first, then offline users
            return users.sort((a, b) => {
                // If one user is online and the other is not, prioritize the online user
                if (a.status === 'online' && b.status !== 'online') return -1;
                if (a.status !== 'online' && b.status === 'online') return 1;
                
                // If both have the same status, sort alphabetically by username
                return a.username.toLowerCase().localeCompare(b.username.toLowerCase());
            });
        },
        
        onlineUsersCount() {
            return this.data.filter(user => user.status === 'online').length;
        },
        
        filteredBannedIps() {
            let ips = this.bannedIps;
            
            // Filter by search if there's a search term
            if (this.ipSearch) {
                ips = ips.filter(ip => 
                    ip.ipAddress.toLowerCase().includes(this.ipSearch.toLowerCase()) ||
                    (ip.reason && ip.reason.toLowerCase().includes(this.ipSearch.toLowerCase()))
                );
            }
            
            // Sort by most recently banned first
            return ips.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }
    },
    watch: {
        // onlineUsers: {
        //     handler(newUsers, oldUsers) {
        //         console.log('🔄 Online users array changed:');
        //         console.log('  Old users:', oldUsers?.map(u => u.username) || []);
        //         console.log('  New users:', newUsers?.map(u => u.username) || []);
        //         console.log('  Change trigger: updateUserStatuses will be called');
        //     },
        //     deep: true
        // },

        // connectionStatus(newStatus, oldStatus) {
        //     console.log(`🔌 Connection status changed: ${oldStatus} → ${newStatus}`);
        //     if (newStatus === 'connected') {
        //         console.log('✅ Connected! Will request online users in 2 seconds...');
        //         setTimeout(() => {
        //             this.requestOnlineUsers();
        //         }, 2000);
        //     }
        // }
    },
    methods: {
        async loadLiveChatUsers() {
            try {
                console.log('🔄 Loading users from live chat server...');
                const response = await axios.get(
                    `${process.env.VUE_APP_LIVECHAT_SERVER_URL}/api/users`,
                    {
                        headers: {
                            'x-api-key': process.env.VUE_APP_CHAT_CLIENT_API_KEY,
                            'Content-Type': 'application/json'
                        }
                    }
                );
                if (response.status === 200 && response.data && response.data.data && Array.isArray(response.data.data.users)) {
                    this.user = response.data.data.users;
                    const liveChatUsers = response.data.data.users.map(user => ({
                        _id: user.id || user._id,
                        username: user.username,
                        status: 'offline',
                        isVerified: user.isVerified || false,
                        isMuted: false,
                        avatar: user.avatar || '',
                        isBanned: user.restriction?.isBanned || false,
                        banType: user.restriction?.banType || undefined,
                        banExpiresAt: user.restriction?.banExpiresAt || undefined,
                        banCount: user.restriction?.banCount || 0,
                        createdAt: new Date().toISOString(),
                        source: 'livechat',
                        userType: user.userType,
                        metadata: user.metadata
                    }));

                    liveChatUsers.forEach(liveChatUser => {
                        const existingUserIndex = this.data.findIndex(
                            user => user.username.toLowerCase() === liveChatUser.username.toLowerCase()
                        );
                        
                        if (existingUserIndex === -1) {
                            // Add new user from live chat
                            this.data.push(liveChatUser);
                        } else {
                            // Update existing user with live chat data
                            this.data[existingUserIndex] = {
                                ...this.data[existingUserIndex],
                                ...liveChatUser,
                                source: 'both' // Mark as existing in both systems
                            };
                        }
                    });
                }
            } catch (error) {
                console.error('❌ Error loading live chat users:', error);
                if (error.response) {
                    console.error('Response error:', error.response.data);
                    this.showNotification(
                        `Failed to load live chat users: ${error.response.status} ${error.response.statusText}`, 
                        'error'
                    );
                } else if (error.request) {
                    console.error('Network error:', error.request);
                    this.showNotification('Network error while loading live chat users', 'error');
                } else {
                    console.error('Error:', error.message);
                    this.showNotification(`Error loading live chat users: ${error.message}`, 'error');
                }
            }
        },

        // Method to refresh live chat users manually
        async refreshLiveChatUsers() {
            console.log('🔄 Manually refreshing live chat users...');
            await this.loadLiveChatUsers();
        },
        
        loadMessages() {
            this.messages = [];
            this.scrollToBottom();
            
            // If socket is already connected, try to load message history
            if (this.socket && this.connectionStatus === 'connected') {
                this.loadMessageHistory();
            }
        },
        
        // Socket.IO Connection Methods
        connectToChat() {
            if (!this.selectedClient) {
                this.showNotification('Client configuration not found', 'error');
                return;
            }

            if (!this.username.trim()) {
                this.showNotification('Please enter a username', 'error');
                return;
            }

            // Show connecting status
            this.connectionStatus = 'connecting';
            this.showNotification(`Connecting to ${this.selectedClient.name}...`, 'info');

            // Connect to chat server
            this.socket = io(process.env.VUE_APP_LIVECHAT_SERVER_URL);
            
            this.setupSocketListeners();

            // Authenticate with the selected client
            this.socket.emit('client:authenticate', {
                clientId: this.selectedClient.clientId,
                apiKey: this.selectedClient.apiKey,
                username: this.username,
                userType: this.userType
            });
        },

        reconnectToChat() {
            // Automatically set credentials for reconnection
            this.username = 'Admin';
            this.userType = 'moderator';
            
            // Call the regular connect method
            this.connectToChat();
        },

        setupSocketListeners() {
            if (!this.socket) return;

            this.socket.on('connect', () => {
                console.log('Connected to server');
            });

            this.socket.on('client:authenticated', (data) => {
                console.log('Client authenticated:', data);
                if (data.success) {
                    this.currentUser = data.user;
                    this.connectionStatus = 'connected';
                    this.onlineCount = data.onlineCount;
                    this.isAutoConnecting = false;
                    
                    // Set online users if provided
                    if (data.onlineUsers && Array.isArray(data.onlineUsers)) {
                        this.onlineUsers = data.onlineUsers;
                        this.updateUserStatuses();
                    }
                    
                    // Load recent messages
                    if (data.recentMessages && data.recentMessages.length > 0) {
                        this.messages = [];
                        data.recentMessages.reverse().forEach(message => {
                            this.displayMessage(message);
                        });
                    }
                    
                    // Request message history after successful connection
                    this.loadMessageHistory();
                    
                    // Request current online users list
                    this.requestOnlineUsers();
                    
                    this.showNotification(`Connected to ${this.selectedClient.name} successfully`, 'success');
                } else {
                    this.isAutoConnecting = false;
                    this.connectionStatus = 'disconnected';
                }
            });

            this.socket.on('message:new', (message) => {
                this.displayMessage(message);
            });

            this.socket.on('message:deleted', (data) => {
                console.log(`Message ${data.messageId} was deleted by ${data.deletedBy}`);
                this.removeMessageFromChat(data.messageId);
            });

            this.socket.on('messages:history', async (data) => {
                await this.handleGetMessageHistory(data);
            });

            this.socket.on('users:count', (data) => {
                console.log('Online users count updated:', data);
                this.onlineCount = data.count;
                
                // Update online users list
                if (data.onlineUsers && Array.isArray(data.onlineUsers)) {
                    console.log('Received full online users list:', data.onlineUsers);
                    this.onlineUsers = data.onlineUsers;
                } else if (data.latestUser) {
                    console.log('Received latest user:', data.latestUser);
                    // If only latest user is provided, add to online users if not already present
                    const existingIndex = this.onlineUsers.findIndex(user => user.username === data.latestUser.username);
                    if (existingIndex === -1) {
                        this.onlineUsers.push(data.latestUser);
                        console.log('Added latest user to online list:', data.latestUser.username);
                    } else {
                        console.log('Latest user already in online list:', data.latestUser.username);
                    }
                }
                
                // Always update user statuses when count changes
                this.updateUserStatuses();
            });

            this.socket.on('typing:start', (data) => {
                if (data.username !== this.currentUser?.username) {
                    if (!this.typingUsers.includes(data.username)) {
                        this.typingUsers.push(data.username);
                    }
                }
            });

            this.socket.on('typing:stop', (data) => {
                const index = this.typingUsers.indexOf(data.username);
                if (index > -1) {
                    this.typingUsers.splice(index, 1);
                }
            });

            this.socket.on('messages:history:response', (response) => {
                console.log('Message history response received:', response);
                // Handle the message history response
                // You can update UI, display messages, etc.
                this.handleMessageHistoryResponse(response);
            });

            this.socket.on('messages:history:error', (error) => {
                console.error('Message history error:', error);
                this.showNotification(`Failed to load message history: ${error.error}`, 'error');
            });

            this.socket.on('error', (error) => {
                this.showNotification(error.message, 'error');
                this.connectionStatus = 'disconnected';
                this.isAutoConnecting = false;
            });

            this.socket.on('disconnect', () => {
                this.connectionStatus = 'disconnected';
                this.isAutoConnecting = false;
                this.onlineUsers = []; // Clear online users when disconnected
                this.updateUserStatuses(); // Update all users to offline
                this.showNotification('Disconnected from server', 'error');
            });

            // Listen for user join/leave events
            this.socket.on('user:joined', (userData) => {
                console.log('User joined:', userData);
                const existingIndex = this.onlineUsers.findIndex(user => user.username === userData.username);
                if (existingIndex === -1) {
                    this.onlineUsers.push(userData);
                    console.log('Added user to online list:', userData.username);
                } else {
                    console.log('User already in online list:', userData.username);
                }
                // Always update statuses when a user joins
                this.updateUserStatuses();
            });

            this.socket.on('user:left', (userData) => {
                console.log('User left:', userData);
                this.handleUserDisconnection(userData);
            });

            // Listen for online users list response
            this.socket.on('users:online:response', (data) => {
                console.log('Online users list received:', data);
                if (data.onlineUsers && Array.isArray(data.onlineUsers)) {
                    this.onlineUsers = data.onlineUsers;
                    this.onlineCount = data.count || data.onlineUsers.length;
                    this.updateUserStatuses();
                }
            });

            // Listen for users list updates (from backend disconnections)
            this.socket.on('users:list', (data) => {
                console.log('Users list updated:', data);
                if (data.users && Array.isArray(data.users)) {
                    this.onlineUsers = data.users;
                    this.updateUserStatuses();
                }
            });

            // Listen for admin notifications about user disconnections
            this.socket.on('admin:user:disconnected', (data) => {
                console.log('Admin notification - User disconnected:', data);
                
                // Handle the disconnection using our robust method
                this.handleUserDisconnection(data);
                
                // Show notification about user disconnection
                this.showNotification(
                    `${data.user?.username || 'User'} disconnected from ${data.client?.name || 'chat'}`, 
                    'info'
                );
            });

            // Listen for user ban events
            this.socket.on('user:banned', (data) => {
                console.log('User banned event received:', data);
                
                // Update user in local data
                const userIndex = this.data.findIndex(u => u.username === data.user.username);
                if (userIndex > -1) {
                    this.data[userIndex].isBanned = true;
                    this.data[userIndex].banType = data.banType;
                    this.data[userIndex].banExpiresAt = data.banExpiresAt;
                    this.data[userIndex].banCount = data.user.banCount;
                }
                
                // Show notification
                this.showNotification(
                    `${data.user.username} has been ${data.banType} banned`, 
                    'warning'
                );
            });

            // Listen for user unban events
            this.socket.on('user:unbanned', (data) => {
                console.log('User unbanned event received:', data);
                
                // Update user in local data
                const userIndex = this.data.findIndex(u => u.username === data.user.username);
                if (userIndex > -1) {
                    this.data[userIndex].isBanned = false;
                    this.data[userIndex].banType = undefined;
                    this.data[userIndex].banExpiresAt = undefined;
                }
                
                // Show notification
                this.showNotification(
                    `${data.user.username} has been unbanned`, 
                    'success'
                );
            });

            // Listen for chat clear events
            this.socket.on('admin:clear:chat:success', (data) => {
                console.log('Chat cleared successfully:', data);
                
                // Clear local messages
                this.messages = [];
                
                // Update UI
                this.clearLoading = false;
                this.clearDialog = false;
                
                // Show success notification
                this.showNotification(
                    `All messages have been cleared by ${data.clearedBy || 'admin'}`, 
                    'success'
                );
                
                // Emit event for parent components
                this.$emit('all-messages-cleared');
            });

            this.socket.on('admin:clear:chat:error', (error) => {
                console.error('Chat clear error:', error);
                
                // Update UI
                this.clearLoading = false;
                this.clearDialog = false;
                
                // Show error notification
                this.showNotification(
                    error.message || 'Failed to clear chat messages', 
                    'error'
                );
            });

            // Listen for chat clear notifications (when another admin clears chat)
            this.socket.on('chat:cleared', (data) => {
                console.log('Chat was cleared by another admin:', data);
                
                // Clear local messages
                this.messages = [];
                
                // Show notification
                this.showNotification(
                    `Chat was cleared by ${data.clearedBy || 'an admin'}`, 
                    'info'
                );
                
                // Emit event for parent components
                this.$emit('all-messages-cleared');
            });
        },

        displayMessage(message) {
            console.log('New message received:', message);
            const messageObj = {
                _id: message._id || Date.now().toString(),
                userId: message.senderId || message.sender?._id,
                username: message.sender?.username || 'Unknown',
                avatar: message.sender?.avatar || '',
                userType: message.sender?.userType || 'user',
                content: message.content,
                timestamp: new Date(message.createdAt || Date.now()),
                flagged: false,
                isSystem: message.messageType === 'system'
            };
            
            this.messages.push(messageObj);
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },

        async handleGetMessageHistory(data) {
            try {
                console.log('Received message history response:', data);
                
                // Validate the response data
                if (!data || typeof data !== 'object') {
                    console.error('Invalid message history response data');
                    return;
                }

                // Check if this is a response with messages array (from server)
                if (data.messages && Array.isArray(data.messages)) {
                    console.log('Processing message history response with', data.messages.length, 'messages');
                    
                    // Display all received messages
                    this.displayMessageHistory(data);
                    return;
                }

                // If it's a request (legacy support), handle as before
                const { page = 1, limit = 50, before, after, userId } = data;

                // For now, we'll work with the current messages array
                // In a real implementation, you might fetch from an API
                let filteredMessages = [...this.messages];

                // Filter by userId if provided
                if (userId) {
                    filteredMessages = filteredMessages.filter(msg => msg.userId === userId);
                }

                // Filter by timestamp if before/after provided
                if (before) {
                    const beforeDate = new Date(before);
                    filteredMessages = filteredMessages.filter(msg => new Date(msg.timestamp) < beforeDate);
                }

                if (after) {
                    const afterDate = new Date(after);
                    filteredMessages = filteredMessages.filter(msg => new Date(msg.timestamp) > afterDate);
                }

                // Sort by timestamp (newest first)
                filteredMessages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

                // Implement pagination
                const startIndex = (page - 1) * limit;
                const endIndex = startIndex + limit;
                const paginatedMessages = filteredMessages.slice(startIndex, endIndex);

                // Prepare response
                const response = {
                    messages: paginatedMessages,
                    pagination: {
                        page,
                        limit,
                        total: filteredMessages.length,
                        totalPages: Math.ceil(filteredMessages.length / limit),
                        hasNext: endIndex < filteredMessages.length,
                        hasPrev: page > 1
                    },
                    filters: {
                        userId,
                        before,
                        after
                    }
                };

                // Emit the response back through socket
                if (this.socket) {
                    this.socket.emit('messages:history:response', response);
                }

                console.log('Message history response sent:', response);

            } catch (error) {
                console.error('Error handling message history:', error);
                
                // Send error response if socket is available
                if (this.socket) {
                    this.socket.emit('messages:history:error', {
                        error: 'Failed to process message history',
                        details: error.message
                    });
                }
            }
        },

        // Display message history received from server
        displayMessageHistory(data) {
            try {
                const messages = data.messages || data || [];
                if (!Array.isArray(messages)) {
                    console.warn('Messages is not an array:', messages);
                    return;
                }

                if (messages.length === 0) {
                    console.log('No messages to display');
                    return;
                }

                // Clear existing messages and display all new ones
                this.messages = [];
                
                // Format and add all messages
                const formattedMessages = messages.map(msg => this.formatMessage(msg));
                // Sort messages by timestamp (oldest first, latest last)
                const sortedMessages = formattedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                
                // Set all messages at once
                this.messages = sortedMessages;

                // Scroll to bottom and force update
                this.$nextTick(() => {
                    this.scrollToBottom();
                    this.$forceUpdate();
                });

            } catch (error) {
                console.error('Error displaying message history:', error);
                console.error('Data received:', data);
            }
        },

        handleMessageHistoryResponse(response) {
            try {
                console.log('Message history response received:', response);

                if (!response || !response.messages) {
                    console.warn('Invalid message history response');
                    return;
                }

                this.displayMessageHistory(response);

            } catch (error) {
                console.error('Error processing message history response:', error);
            }
        },

        formatMessage(message) {
            const formatted = {
                _id: message._id || message.id || Date.now().toString(),
                userId: message.sender._id,
                username: message.sender.username || 'Unknown',
                avatar: message.sender.avatar || '',
                userType: message.sender.userType || 'user',
                content: message.content,
                timestamp: new Date(message.timestamp || message.createdAt || Date.now()),
                flagged: message.flagged || false,
                isSystem: message.isSystem || message.messageType === 'system'
            };
            
            return formatted;
        },

        requestMessageHistory(options = {}) {
            if (!this.socket || !this.currentUser) {
                console.warn('Cannot request message history: not connected');
                return;
            }

            const requestData = {
                getAllMessages: true,
                ...options
            };
            this.socket.emit('messages:history', requestData);
        },

        // Request current online users list
        requestOnlineUsers() {
            if (!this.socket || !this.currentUser) {
                console.warn('Cannot request online users: not connected');
                return;
            }

            console.log('Requesting online users list...');
            this.socket.emit('users:online:request');
        },

        // Force sync user statuses - useful for debugging
        forceSyncUserStatuses() {
            console.log('🔄 Force syncing user statuses...');
            this.requestOnlineUsers();
            this.updateUserStatuses();
        },

        sendSocketMessage() {
            if (!this.newMessage.trim() || !this.socket || !this.currentUser) {
                return;
            }
            
            try {
                this.socket.emit('message:send', {
                    content: this.newMessage.trim()
                });
                
                this.newMessage = '';
                this.stopTyping();
                
            } catch (error) {
                console.error('Error sending message:', error);
                this.showNotification('Failed to send message', 'error');
            }
        },

        handleTyping() {
            if (!this.socket || !this.currentUser) return;
            
            if (!this.isTyping) {
                this.isTyping = true;
                this.socket.emit('typing:start');
            }
            
            clearTimeout(this.typingTimer);
            this.typingTimer = setTimeout(this.stopTyping, 1000);
        },

        stopTyping() {
            if (this.isTyping && this.socket) {
                this.isTyping = false;
                this.socket.emit('typing:stop');
            }
        },

        selectClient(client) {
            this.selectedClient = client;
            this.showNotification(`Selected: ${client.name}`, 'success');
        },

        disconnectFromChat() {
            if (this.socket) {
                this.socket.disconnect();
                this.socket = null;
            }
            this.currentUser = null;
            this.connectionStatus = 'disconnected';
            this.isAutoConnecting = false;
            this.messages = [];
            this.onlineUsers = []; // Clear online users
            this.username = 'Admin';
            this.updateUserStatuses(); // Set all users to offline
            this.loadMessages(); // Load mock messages again
        },

        showNotification(message, type) {
            // You can integrate this with your existing notification system
            // For now, using console and basic alert
            console.log(`${type.toUpperCase()}: ${message}`);
            
            if (type === 'error') {
                // Handle error notifications
                // alert(`Error: ${message}`);
                console.log('Disconnected due to error:', message);
            } else {
                // Handle success notifications
                console.log(`Success: ${message}`);
            }
        },
        
        // Load message history on connection
        loadMessageHistory() {
            if (!this.socket || !this.currentUser) {
                console.warn('Cannot load message history: not connected');
                return;
            }
            
            // Request all messages at once
            console.log('Loading all message history...');
            this.requestMessageHistory();
        },

        // Update user statuses based on online users from socket
        updateUserStatuses() {
            if (!this.data || this.data.length === 0) {
                console.warn('No user data available for status update');
                return;
            }

            // Track status changes for logging
            const statusChanges = {
                toOnline: [],
                toOffline: []
            };

            // Set all users to offline first and track changes
            this.data.forEach(user => {
                if (user.status === 'online') {
                    statusChanges.toOffline.push(user.username);
                }
                user.status = 'offline';
            });

            // Set users to online if they exist in onlineUsers
            this.onlineUsers.forEach(onlineUser => {
                console.log('Checking online user:', onlineUser);
                
                const userIndex = this.data.findIndex(user => {
                    const match = user.username === onlineUser.username || 
                                 user.username.toLowerCase() === onlineUser.username.toLowerCase();
                    if (match) {
                        console.log(`Found match: ${user.username} === ${onlineUser.username}`);
                    }
                    return match;
                });
                
                if (userIndex > -1) {
                    this.data[userIndex].status = 'online';
                    statusChanges.toOnline.push(this.data[userIndex].username);
                    // Remove from offline list if it was there
                    const offlineIndex = statusChanges.toOffline.indexOf(this.data[userIndex].username);
                    if (offlineIndex > -1) {
                        statusChanges.toOffline.splice(offlineIndex, 1);
                    }
                    console.log(`✅ Set ${this.data[userIndex].username} to online`);
                } else {
                    console.log(`❌ No matching user found for online user: ${onlineUser.username}`);
                }
            });

            // Log status changes
            if (statusChanges.toOnline.length > 0) {
                console.log('🟢 Users set to online:', statusChanges.toOnline);
            }
            if (statusChanges.toOffline.length > 0) {
                console.log('🔴 Users set to offline:', statusChanges.toOffline);
            }

            // Log final counts
            const onlineUsersCount = this.data.filter(user => user.status === 'online').length;
            console.log(`📊 Status update complete: ${onlineUsersCount} users online, ${this.data.length - onlineUsersCount} users offline`);
            console.log('=== END UPDATE USER STATUSES ===');

            // Force reactivity update
            this.$forceUpdate();
        },

        // Handle user disconnection with robust cleanup
        handleUserDisconnection(userData) {
            console.log('Handling user disconnection:', userData);
            
            // Remove from online users by username
            const userIndex = this.onlineUsers.findIndex(user => 
                user.username === userData.username ||
                user.username === userData.user?.username ||
                user.id === userData.user?.id
            );
            
            if (userIndex > -1) {
                const disconnectedUser = this.onlineUsers[userIndex];
                this.onlineUsers.splice(userIndex, 1);
                console.log(`Removed ${disconnectedUser.username} from online users list`);
            }
            
            // Update user statuses
            this.updateUserStatuses();
            
            // Update online count if provided
            if (userData.onlineCount !== undefined) {
                this.onlineCount = userData.onlineCount;
                console.log(`Updated online count to: ${this.onlineCount}`);
            }
        },
        
        // Message actions
        deleteMessage(message) {
            if (confirm(`Are you sure you want to delete this message from ${message.username}?`)) {
                if (!this.socket || this.connectionStatus !== 'connected') {
                    this.showNotification('Cannot delete message: not connected to chat server', 'error');
                    return;
                }

                try {
                    // Emit delete request to server
                    this.socket.emit('admin:message:delete', {
                        messageId: message._id
                    });
                    
                    // Show immediate feedback
                    this.showNotification('Delete request sent...', 'info');
                    
                } catch (error) {
                    console.error('Error deleting message:', error);
                    this.showNotification('Failed to delete message', 'error');
                }
            }
        },

        // Helper method to remove message from UI (called by socket listener)
        removeMessageFromChat(messageId) {
            const index = this.messages.findIndex(msg => msg._id === messageId);
            if (index > -1) {
                const removedMessage = this.messages[index];
                this.messages.splice(index, 1);
                console.log(`Removed message from UI: ${messageId}`);
                this.showNotification(`Message from ${removedMessage.username} has been deleted`, 'success');
                this.$emit('message-deleted', messageId);
            } else {
                console.warn(`Message with ID ${messageId} not found in local messages`);
            }
        },
        
        flagMessage(message) {
            const index = this.messages.findIndex(msg => msg._id === message._id);
            if (index > -1) {
                this.messages[index].flagged = !this.messages[index].flagged;
                // TODO: Call API to flag/unflag message
                this.$emit('message-flagged', { id: message._id, flagged: this.messages[index].flagged });
            }
        },
        
        showUserActions(userId) {
            const user = this.data.find(u => u._id === userId);
            if (user) {
                this.selectedUserProfile = user;
                this.profileDialog = true;
            }
        },
        
        // Broadcasting
        broadcastMessage() {
            this.broadcastDialog = true;
        },
        
        // Chat management
        clearAllChats() {
            this.clearDialog = true;
        },
        
        confirmClearChats() {
            if (!this.socket || this.connectionStatus !== 'connected') {
                this.showNotification('Cannot clear messages: not connected to chat server', 'error');
                this.clearDialog = false;
                return;
            }

            this.clearLoading = true;
            
            try {
                // Emit clear chat request to server
                this.socket.emit('admin:clear:chat', {
                    timestamp: new Date().toISOString(),
                    adminUsername: this.username
                });
                
                // Clear local messages immediately
                this.messages = [];
                
                // Show immediate feedback
                this.showNotification('Chat cleared successfully', 'success');
                
                // Update UI
                this.clearLoading = false;
                this.clearDialog = false;
                
                // Emit event for parent components
                this.$emit('all-messages-cleared');
                
            } catch (error) {
                console.error('Error clearing chat:', error);
                this.showNotification('Failed to send clear chat request', 'error');
                this.clearLoading = false;
                this.clearDialog = false;
            }
        },

        // User management methods
        showVerifyDialog(data){
            this.itemToVerify = data
            this.verifyDialog = true
        },
        
        showBanDialog(data){
            this.itemToBan = data
            this.banDialog = true
            this.banReason = ''
            this.banType = 'permanent'
            this.banDuration = 3600 // Reset to 1 hour default
        },
        
        showMuteDialog(data){
            this.itemToMute = data
            this.muteDialog = true
            this.muteReason = ''
            this.muteDuration = '1h'
        },
        
        viewUserProfile(user) {
          console.log(user)
            this.selectedUserProfile = user;
            this.profileDialog = true;
        },
        
        verifyUser(){
            const token = localStorage.getItem('seham-admin-accesstoken')
            let formData = {
                userId: this.itemToVerify._id
            }
            this.verifyLoading = true
            axios.post(process.env.VUE_APP_SERVER_URL + '/api/admin/user/verify', formData,
            { headers: 
                { 
                    'Authorization': `Bearer ${token}`,
                    'x-admin-key': process.env.VUE_APP_X_ADMIN_KEY,
                }
            }
            ).then((res)=>{
                if(res){
                    this.verifyLoading = false
                    this.verifyDialog = false
                }
            }).catch((error) => {
                this.handleError(error);
                this.verifyLoading = false
            });
        },
        
        banUser(){
            const token = localStorage.getItem('seham-admin-accesstoken')
            
            // Prepare form data based on ban/unban action
            let formData = {}
            let endpoint = ''
            
            if (this.itemToBan.isBanned) {
                // Unban user
                formData = {
                    username: this.itemToBan.username
                }
                endpoint = `${process.env.VUE_APP_LIVECHAT_SERVER_URL}/api/users/unban`
            } else {
                // Ban user
                formData = {
                    username: this.itemToBan.username,
                    banType: this.banType,
                    reason: this.banReason || undefined
                }
                
                // Add duration only for temporary bans
                if (this.banType === 'temporary' && this.banDuration) {
                    formData.duration = this.banDuration
                }
                
                endpoint = `${process.env.VUE_APP_LIVECHAT_SERVER_URL}/api/users/ban`
            }
            
            this.banLoading = true
            
            axios.post(endpoint, formData, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': process.env.VUE_APP_CHAT_CLIENT_API_KEY,
                    'Content-Type': 'application/json'
                }
            })
            .then((res) => {
                if (res && res.data && res.data.success) {
                    // Update local data with response
                    const userIndex = this.data.findIndex(u => u.username === this.itemToBan.username);
                    if (userIndex > -1) {
                        if (this.itemToBan.isBanned) {
                            // User was unbanned
                            this.data[userIndex].isBanned = false
                            this.data[userIndex].banType = undefined
                            this.data[userIndex].banExpiresAt = undefined
                        } else {
                            // User was banned
                            this.data[userIndex].isBanned = true
                            this.data[userIndex].banType = this.banType
                            this.data[userIndex].banExpiresAt = res.data.data?.user?.banExpiresAt || undefined
                        }
                    }
                    
                    // Show success message
                    const action = this.itemToBan.isBanned ? 'unbanned' : 'banned'
                    this.showNotification(`${this.itemToBan.username} has been ${action} successfully`, 'success')
                    
                    this.banLoading = false
                    this.banDialog = false
                    this.banReason = ''
                    this.banType = 'permanent'
                    this.banDuration = 3600
                } else {
                    throw new Error(res.data?.error || 'Unknown error occurred')
                }
            })
            .catch((error) => {
                console.error('Ban/Unban error:', error)
                let errorMessage = 'Failed to ban/unban user'
                
                if (error.response && error.response.data && error.response.data.error) {
                    errorMessage = error.response.data.error
                } else if (error.message) {
                    errorMessage = error.message
                }
                
                this.showNotification(errorMessage, 'error')
                this.banLoading = false
            });
        },
        
        muteUser(){
            const token = localStorage.getItem('seham-admin-accesstoken')
            let formData = {
                userId: this.itemToMute._id,
                duration: this.muteDuration,
                reason: this.muteReason,
                action: this.itemToMute.isMuted ? 'unmute' : 'mute'
            }
            this.muteLoading = true
            
            const endpoint = this.itemToMute.isMuted ? '/api/admin/user/unmute' : '/api/admin/user/mute';
            
            axios.post(process.env.VUE_APP_SERVER_URL + endpoint, formData,
            { headers: 
                { 
                    'Authorization': `Bearer ${token}`,
                    'x-admin-key': process.env.VUE_APP_X_ADMIN_KEY,
                }
            }
            ).then((res)=>{
                if(res){
                    // Update local data
                    const userIndex = this.data.findIndex(u => u._id === this.itemToMute._id);
                    if (userIndex > -1) {
                        this.data[userIndex].isMuted = !this.data[userIndex].isMuted;
                    }
                    
                    this.muteLoading = false
                    this.muteDialog = false
                    this.muteReason = ''
                    this.muteDuration = '1h'
                }
            }).catch((error) => {
                this.handleError(error);
                this.muteLoading = false
            });
        },
        
        // Utility methods
        getBanLabel(user) {
            if (!user.isBanned) return '';
            
            if (user.banType === 'temporary') {
                return 'TEMP BAN';
            }
            return 'BANNED';
        },
        
        getBanTooltip(user) {
            if (!user.isBanned) return '';
            
            let tooltip = `Ban Type: ${user.banType || 'permanent'}`;
            
            if (user.banExpiresAt) {
                const expiryDate = new Date(user.banExpiresAt);
                const now = new Date();
                
                if (expiryDate > now) {
                    tooltip += `\nExpires: ${this.formatDateTime(user.banExpiresAt)}`;
                } else {
                    tooltip += '\nExpired (should be unbanned)';
                }
            } else {
                tooltip += '\nPermanent ban';
            }
            
            return tooltip;
        },
        
        getUserMessageCount(userId) {
            return this.messages.filter(msg => msg.userId === userId).length;
        },
        
        getUserFlaggedCount(userId) {
            return this.messages.filter(msg => msg.userId === userId && msg.flagged).length;
        },
        
        formatDateTime(dateString) {
            const date = new Date(dateString);
            return date.toLocaleString();
        },

        // IP Ban Management Methods
        async refreshBannedIps() {
            try {
                console.log('🔄 Refreshing banned IP addresses...');
                
                const response = await axios.get(
                    `${process.env.VUE_APP_LIVECHAT_SERVER_URL}/api/users/ip/banned`,
                    {
                        headers: {
                            'x-api-key': process.env.VUE_APP_CHAT_CLIENT_API_KEY,
                            'Content-Type': 'application/json'
                        }
                    }
                );
                
                if (response.status === 200 && response.data && response.data.success) {
                    const rawData = response.data.data?.bannedIps || response.data.data || [];
                    this.bannedIps = this.normalizeBannedIpData(rawData);
                    console.log(`✅ Loaded ${this.bannedIps.length} banned IP addresses`);
                    if (this.bannedIps.length > 0) {
                        this.showNotification(`Loaded ${this.bannedIps.length} banned IP addresses`, 'info');
                    }
                }
            } catch (error) {
                console.error('❌ Error refreshing banned IPs:', error);
                this.showNotification('Failed to load banned IP addresses', 'error');
            }
        },

        async banIpAddress() {
            if (!this.ipToBan || !this.isValidIp(this.ipToBan)) {
                this.showNotification('Please enter a valid IP address', 'error');
                return;
            }

            this.ipBanLoading = true;
            
            try {
                console.log('🚫 Banning IP address:', this.ipToBan);
                
                const payload = {
                    ipAddress: this.ipToBan,
                    banType: this.ipBanType,
                    reason: this.ipBanReason || 'No reason provided',
                    bannedBy: this.username
                };

                if (this.ipBanType === 'temporary') {
                    payload.duration = this.ipBanDuration;
                }

                const response = await axios.post(
                    `${process.env.VUE_APP_LIVECHAT_SERVER_URL}/api/users/ip/ban`,
                    payload,
                    {
                        headers: {
                            'x-api-key': process.env.VUE_APP_CHAT_CLIENT_API_KEY,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (response.status === 200 && response.data && response.data.success) {
                    console.log('✅ IP address banned successfully');
                    this.showNotification(`IP ${this.ipToBan} has been banned successfully`, 'success');
                    
                    // Refresh the banned IPs list
                    await this.refreshBannedIps();
                    
                    // Close dialog and reset form
                    this.closeIpBanDialog();
                } else {
                    throw new Error(response.data?.error || 'Ban request failed');
                }
            } catch (error) {
                console.error('❌ Error banning IP:', error);
                if (error.response?.data?.error) {
                    this.showNotification(error.response.data.error, 'error');
                } else if (error.response?.data?.message) {
                    this.showNotification(error.response.data.message, 'error');
                } else {
                    this.showNotification('Failed to ban IP address', 'error');
                }
            } finally {
                this.ipBanLoading = false;
            }
        },

        async unbanIp(bannedIp) {
            if (!bannedIp || (!bannedIp.id && !bannedIp.ipAddress)) {
                this.showNotification('Invalid IP ban record', 'error');
                return;
            }

            this.unbanIpLoading = bannedIp.id || bannedIp.ipAddress;
            
            try {
                console.log('🔓 Unbanning IP address:', bannedIp.ipAddress);
                
                const response = await axios.post(
                    `${process.env.VUE_APP_LIVECHAT_SERVER_URL}/api/users/ip/unban`,
                    { 
                        ipAddress: bannedIp.ipAddress,
                        unbannedBy: this.username
                    },
                    {
                        headers: {
                            'x-api-key': process.env.VUE_APP_CHAT_CLIENT_API_KEY,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (response.status === 200 && response.data && response.data.success) {
                    console.log('✅ IP address unbanned successfully');
                    this.showNotification(`IP ${bannedIp.ipAddress} has been unbanned successfully`, 'success');
                    
                    // Refresh the banned IPs list
                    await this.refreshBannedIps();
                    
                    // Close details dialog if it's open
                    if (this.showIpBanDetailsDialog) {
                        this.showIpBanDetailsDialog = false;
                    }
                } else {
                    throw new Error(response.data?.error || 'Unban request failed');
                }
            } catch (error) {
                console.error('❌ Error unbanning IP:', error);
                if (error.response?.data?.error) {
                    this.showNotification(error.response.data.error, 'error');
                } else if (error.response?.data?.message) {
                    this.showNotification(error.response.data.message, 'error');
                } else {
                    this.showNotification('Failed to unban IP address', 'error');
                }
            } finally {
                this.unbanIpLoading = null;
            }
        },

        viewIpBanDetails(bannedIp) {
            this.selectedIpBan = bannedIp;
            this.showIpBanDetailsDialog = true;
        },

        editIpBan(bannedIp) {
            // Pre-populate the ban dialog with existing data for editing
            this.ipToBan = bannedIp.ipAddress;
            this.ipBanType = bannedIp.banType || 'permanent';
            this.ipBanReason = bannedIp.reason || '';
            this.showIpBanDialog = true;
        },

        closeIpBanDialog() {
            this.showIpBanDialog = false;
            // Reset form
            this.ipToBan = '';
            this.ipBanType = 'permanent';
            this.ipBanDuration = 3600;
            this.ipBanReason = '';
        },

        // Normalize banned IP data from API response
        normalizeBannedIpData(rawData) {
            if (!rawData || !Array.isArray(rawData)) {
                return [];
            }

            return rawData.map(item => ({
                id: item._id || item.id || item.ipAddress,
                ipAddress: item.ipAddress || item.ip,
                banType: item.banType || 'permanent',
                reason: item.reason || 'No reason provided',
                bannedBy: item.bannedBy || item.createdBy || 'System',
                createdAt: item.createdAt || item.bannedAt || new Date().toISOString(),
                expiresAt: item.expiresAt || item.expiredAt,
                isActive: item.isActive !== false, // Default to true if not specified
                ...item // Keep any additional fields
            }));
        },

        isValidIp(ip) {
            if (!ip) return false;
            
            // IPv4 validation
            const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
            if (ipv4Regex.test(ip)) return true;
            
            // IPv6 validation (basic)
            const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
            if (ipv6Regex.test(ip)) return true;
            
            // IPv6 compressed format
            const ipv6CompressedRegex = /^::1$|^::$|^(?:[0-9a-fA-F]{1,4}:)*::[0-9a-fA-F]{1,4}(?::[0-9a-fA-F]{1,4})*$|^(?:[0-9a-fA-F]{1,4}:)+[0-9a-fA-F]{1,4}$/;
            if (ipv6CompressedRegex.test(ip)) return true;
            
            return false;
        },

        getIpBanLabel(bannedIp) {
            if (bannedIp.banType === 'temporary') {
                const expiresAt = new Date(bannedIp.expiresAt);
                const now = new Date();
                
                if (expiresAt <= now) {
                    return 'EXPIRED';
                }
                
                const diff = expiresAt - now;
                const hours = Math.floor(diff / (1000 * 60 * 60));
                const days = Math.floor(hours / 24);
                
                if (days > 0) {
                    return `${days}d left`;
                } else if (hours > 0) {
                    return `${hours}h left`;
                } else {
                    return '<1h left';
                }
            }
            return 'PERMANENT';
        },

        getIpBanTooltip(bannedIp) {
            let tooltip = `IP: ${bannedIp.ipAddress}\nBan Type: ${bannedIp.banType}`;
            
            if (bannedIp.banType === 'temporary') {
                tooltip += `\nExpires: ${this.formatDateTime(bannedIp.expiresAt)}`;
            }
            
            if (bannedIp.reason) {
                tooltip += `\nReason: ${bannedIp.reason}`;
            }
            
            if (bannedIp.bannedBy) {
                tooltip += `\nBanned by: ${bannedIp.bannedBy}`;
            }
            
            tooltip += `\nBanned on: ${this.formatDateTime(bannedIp.createdAt)}`;
            
            return tooltip;
        },

        // Get avatar background color based on user type
        getUserAvatarColor(userType) {
            switch (userType) {
                case 'moderator':
                    return 'primary';
                case 'registered':
                    return 'success';
                case 'anonymous':
                    return 'grey';
                default:
                    return 'grey-darken-2';
            }
        },

        async checkIpBanStatus(ipAddress) {
          if (!ipAddress || !this.isValidIp(ipAddress)) {
              this.showNotification('Please enter a valid IP address', 'error');
              return null;
          }

          try {
              console.log('🔍 Checking ban status for IP:', ipAddress);
              
              const response = await axios.get(
                  `${process.env.VUE_APP_LIVECHAT_SERVER_URL}/api/users/ip/${ipAddress}/status`,
                  {
                      headers: {
                          'x-api-key': process.env.VUE_APP_CHAT_CLIENT_API_KEY,
                          'Content-Type': 'application/json'
                      }
                  }
              );

              if (response.status === 200 && response.data && response.data.success) {
                  const status = response.data.data;
                  console.log('✅ IP ban status retrieved:', status);
                  
                  if (status.isBanned) {
                      this.showNotification(`IP ${ipAddress} is currently banned (${status.banType})`, 'warning');
                  } else {
                      this.showNotification(`IP ${ipAddress} is not banned`, 'info');
                  }
                  
                  return status;
              } else {
                  throw new Error(response.data?.error || 'Failed to check IP status');
              }
          } catch (error) {
              console.error('❌ Error checking IP ban status:', error);
              if (error.response?.data?.error) {
                  this.showNotification(error.response.data.error, 'error');
              } else {
                  this.showNotification('Failed to check IP ban status', 'error');
              }
              return null;
          }
      },
      toggleAutoScroll() {
          this.autoScroll = !this.autoScroll;
          if (this.autoScroll) {
              this.scrollToBottom();
          }
      },
      
      scrollToBottom() {
          this.$nextTick(() => {
              const container = this.$refs.chatContainer;
              if (container && this.autoScroll) {
                  container.scrollTop = container.scrollHeight;
              }
          });
      },
      
      handleScroll() {
          const container = this.$refs.chatContainer;
          if (container) {
              const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;
              if (isAtBottom && !this.autoScroll) {
                  this.autoScroll = true;
              } else if (!isAtBottom && this.autoScroll) {
                  this.autoScroll = false;
              }
          }
      },
      
      handleError(error) {
          if (error.response) {
              if (error.response.status === 401) {
                  this.redirectToLogin();
              } else {
                  console.log(`Error: ${error.response.status}`);
              }
          } 
          else if (error.request) {
              console.log('No response received', error.request);
          } else {
              console.log('Error', error.message);
          }
      },
      
      redirectToLogin() {
          localStorage.removeItem("vuex");
          localStorage.removeItem("seham-admin-accesstoken");
          window.location.reload();
      },
      
      // startRefreshInterval() {
      //     this.refreshInterval = setInterval(() => {
      //         if (!this.loading) {
      //             this.loadMessages();
      //             // Also refresh live chat users periodically
      //             this.refreshLiveChatUsers();
      //         }
              
      //         // Periodically request online users to ensure sync
      //         if (this.connectionStatus === 'connected' && this.socket) {
      //             this.requestOnlineUsers();
      //         }
      //     }, 30000); // Refresh every 30 seconds
      // },
      
      stopRefreshInterval() {
          if (this.refreshInterval) {
              clearInterval(this.refreshInterval);
              this.refreshInterval = null;
          }
      }
    },
    
    mounted(){
      this.loadLiveChatUsers();
      this.refreshBannedIps();
      // this.startRefreshInterval();
      // Set seham_tv client configuration and connect
      this.selectedClient = this.clientConfig;
      // Auto-connect after a short delay to allow UI to render
      setTimeout(() => {
          this.connectToChat();
      }, 1000);
    },
    
    beforeUnmount() {
        this.stopRefreshInterval();
        // Cleanup Socket.IO connection
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
        // Clear timers
        if (this.typingTimer) {
            clearTimeout(this.typingTimer);
        }
    }
}) 
</script>

<style scoped>
.chat-container {
  padding: 16px;
  background: linear-gradient(135deg, #e8f9fa 0%, #b2ebf2 100%);
  min-height: 100vh;
}

.chat-header-card {
  background: linear-gradient(135deg, #2fc3c8 0%, #0f1e33 100%);
  color: white !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.chat-header-card .v-card-title {
  color: white !important;
}

.chat-messages-card {
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  background: white;
  height: calc(100vh - 200px);
  min-height: 600px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
}

.chat-title {
  background: linear-gradient(135deg, #2fc3c8 0%, #0f1e33 100%);
  color: white !important;
  border-radius: 16px 16px 0 0 !important;
  flex-shrink: 0;
}

.chat-messages-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #fafbfc;
  position: relative;
  min-height: 300px;
}

.message-wrapper {
  margin-bottom: 12px;
}

.message-card {
  border-radius: 12px !important;
  transition: all 0.3s ease;
  background: white;
  border: 1px solid #e3f2fd !important;
}

.message-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.system-message {
  background: linear-gradient(135deg, #e8f9fa 0%, #b2ebf2 100%) !important;
  border: 1px solid #4dd0e1 !important;
}

.broadcast-message {
  background: linear-gradient(135deg, #f0fdff 0%, #e0f2f1 100%) !important;
  border: 1px solid #2fc3c8 !important;
}

.flagged-message .message-card {
  background: linear-gradient(135deg, #ffebee 0%, #fce4ec 100%) !important;
  border: 1px solid #f44336 !important;
  border-left: 4px solid #f44336 !important;
}

.username {
  color: #0f1e33;
  font-weight: 600;
}

.timestamp {
  font-size: 11px;
  opacity: 0.7;
}

.message-content {
  line-height: 1.5;
  word-wrap: break-word;
  color: #2c3e50;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
}

.users-panel-card {
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  background: white;
  height: calc(100vh - 200px);
  min-height: 600px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
}

.users-panel-card .v-card-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 !important;
  overflow: hidden;
}

.users-title {
  background: linear-gradient(135deg, #2fc3c8 0%, #0f1e33 100%);
  color: white !important;
  border-radius: 16px 16px 0 0 !important;
  flex-shrink: 0;
}

.search-field {
  border-radius: 0 !important;
  flex-shrink: 0;
}

.search-field .v-field {
  border-radius: 0 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  box-shadow: none !important;
}

.search-field .v-field__outline {
  display: none !important;
}

.users-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 !important;
  min-height: 0; /* Important for flex child to be scrollable */
}

.users-list .v-list-item {
  padding: 10px 16px !important;
  margin: 0 !important;
  min-height: 64px !important;
  border-radius: 0 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.users-list .v-list-item:last-child {
  border-bottom: none;
}

.user-list-item {
  padding: 10px 16px !important;
  transition: all 0.2s ease;
  border-radius: 0;
  margin: 0;
  min-height: 64px;
}

.users-list .v-list-item:hover {
  background: rgba(47, 195, 200, 0.08) !important;
}

.user-list-item:hover {
  background: rgba(47, 195, 200, 0.08);
}

.empty-users {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

/* IP Ban Management Styles */
.ip-ban-card {
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  background: white;
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.ip-ban-card .v-card-title {
  background: linear-gradient(135deg, #2fc3c8 0%, #0f1e33 100%);
  color: white !important;
  border-radius: 16px 16px 0 0 !important;
  flex-shrink: 0;
}

.ip-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.ip-list-item {
  padding: 12px 16px;
  transition: all 0.2s ease;
  border-radius: 8px;
  margin: 4px 8px;
}

.ip-list-item:hover {
  background: linear-gradient(135deg, #e8f9fa 0%, #b2ebf2 100%);
  transform: translateX(4px);
}

.empty-ips {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.info-item {
  margin-bottom: 16px;
}

.info-item .text-caption {
  margin-bottom: 4px;
}

/* Custom scrollbar styles */
.chat-messages-container::-webkit-scrollbar,
.users-list::-webkit-scrollbar {
  width: 6px;
}

.chat-messages-container::-webkit-scrollbar-track,
.users-list::-webkit-scrollbar-track {
  background: #f1f3f4;
  border-radius: 3px;
}

.chat-messages-container::-webkit-scrollbar-thumb,
.users-list::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #2fc3c8 0%, #0f1e33 100%);
  border-radius: 3px;
}

.chat-messages-container::-webkit-scrollbar-thumb:hover,
.users-list::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #26a6aa 0%, #0a1426 100%);
}

/* Chip styles */
.v-chip {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  border-radius: 8px !important;
}

/* Button animations */
.v-btn {
  transition: all 0.3s ease;
  border-radius: 12px !important;
}

.v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

/* Dialog enhancements */
.v-dialog .v-card {
  border-radius: 20px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

/* Animation for new messages */
@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.message-wrapper:last-child .message-card {
  animation: messageSlideIn 0.4s ease-out;
}

/* Message history load more styles */
.load-more-container {
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  margin: 0 -16px 16px -16px;
  border-radius: 8px;
}

.load-more-container .v-btn {
  border-radius: 20px !important;
  transition: all 0.3s ease;
}

.load-more-container .v-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(47, 195, 200, 0.3);
}

/* Message Input Section Styles */
.message-input-section {
  background: white;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.message-input-container {
  width: 100%;
  background: white;
}

.connection-warning {
  background: #fff8e1;
  flex-shrink: 0;
}

.input-wrapper {
  padding: 16px;
  background: white;
}

.input-container {
  position: relative;
  max-width: 100%;
}

.message-input {
  background: #f8f9fa !important;
}

.message-input .v-field {
  border-radius: 25px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #e1e8ed !important;
  background: #f8f9fa !important;
}

.message-input .v-field--focused {
  border: 2px solid #2fc3c8 !important;
  box-shadow: 0 0 0 3px rgba(47, 195, 200, 0.1) !important;
}

.message-input .v-field__input {
  padding: 12px 16px 12px 48px !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
}

.send-button {
  margin-right: 4px !important;
  border-radius: 50% !important;
  width: 36px !important;
  height: 36px !important;
  box-shadow: 0 2px 8px rgba(47, 195, 200, 0.3) !important;
}

.send-button:hover {
  transform: scale(1.05) !important;
  box-shadow: 0 4px 12px rgba(47, 195, 200, 0.4) !important;
}

.send-button:disabled {
  opacity: 0.6 !important;
  transform: none !important;
  box-shadow: none !important;
}

/* Typing Indicator Styles */
.typing-indicator-container {
  background: #f8f9fa;
  border-top: 1px solid #e1e8ed;
  flex-shrink: 0;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666;
  padding: 4px 0;
}

.typing-dots {
  display: flex;
  gap: 2px;
}

.typing-dots span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #2fc3c8;
  animation: typing-pulse 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing-pulse {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.6;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.typing-text {
  font-style: italic;
}

/* Chat messages container adjustment for fixed input */
.chat-messages-container {
  height: 450px;
  overflow-y: auto;
  padding: 16px;
  background: #fafbfc;
  position: relative;
  margin-bottom: 0;
}

/* Mobile responsiveness for message input */
@media (max-width: 768px) {
  .chat-messages-card,
  .users-panel-card {
    height: calc(100vh - 160px);
    min-height: 500px;
  }
  
  .input-wrapper {
    padding: 12px;
  }
  
  .message-input .v-field__input {
    padding: 10px 14px 10px 44px !important;
    font-size: 16px !important; /* Prevents zoom on iOS */
  }
  
  .send-button {
    width: 32px !important;
    height: 32px !important;
  }
  
  .chat-container {
    padding: 8px;
  }
  
  .message-card {
    margin: 4px 0;
  }
  
  .chat-header-card .v-card-title {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 16px;
  }
}

/* Gradient text effects */
.text-gradient {
  background: linear-gradient(135deg, #2fc3c8 0%, #0f1e33 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Loading states */
.v-progress-linear {
  border-radius: 4px;
}

/* Enhanced avatar styles */
.v-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
}

.message-card .v-avatar {
  flex-shrink: 0;
}

.message-card .v-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* User type chip styles */
.username .v-chip {
  font-size: 0.65rem;
  height: 16px;
}

/* Success/error states */
.success-gradient {
  background: linear-gradient(135deg, #4caf50 0%, #81c784 100%);
}

.error-gradient {
  background: linear-gradient(135deg, #f44336 0%, #ef5350 100%);
}

.warning-gradient {
  background: linear-gradient(135deg, #ff9800 0%, #ffb74d 100%);
}

/* Seham minimal black/gold chat theme */
.chat-container {
  background: #020202 !important;
  color: var(--seham-text);
}

.chat-header-card,
.auth-card,
.chat-messages-card,
.users-panel-card,
.ip-ban-card {
  background: linear-gradient(90deg, rgba(247, 205, 69, 0.1) 0%, rgba(16, 15, 10, 0.96) 26%, rgba(4, 4, 3, 0.98) 100%) !important;
  border: 1px solid rgba(247, 205, 69, 0.32) !important;
  border-radius: 8px !important;
  box-shadow: 0 0 22px rgba(247, 205, 69, 0.08), 0 18px 48px rgba(0, 0, 0, 0.42) !important;
  color: var(--seham-text) !important;
}

.chat-header-card .v-card-title,
.chat-title,
.users-title,
.ip-ban-card .v-card-title,
.auth-card .v-card-title {
  background: rgba(5, 5, 4, 0.72) !important;
  border-bottom: 1px solid rgba(247, 205, 69, 0.24);
  border-radius: 8px 8px 0 0 !important;
  color: #fff7cf !important;
}

.chat-header-card .v-card-title *,
.chat-title *,
.users-title *,
.ip-ban-card .v-card-title *,
.auth-card .v-card-title * {
  color: inherit !important;
}

.chat-header-card h2,
.chat-title .text-h6,
.users-title .text-h6,
.ip-ban-card .text-h6 {
  color: #ffffff !important;
}

.chat-title .v-icon,
.users-title .v-icon,
.ip-ban-card .v-card-title .v-icon {
  color: #f7cd45 !important;
}

.chat-header-card .v-avatar {
  background: #050504 !important;
  border: 1px solid rgba(247, 205, 69, 0.42);
}

.chat-messages-container,
.users-list,
.ip-list,
.ip-ban-card .v-card-text {
  background: #050504 !important;
}

.message-card {
  background: rgba(14, 13, 9, 0.94) !important;
  border: 1px solid rgba(247, 205, 69, 0.18) !important;
  color: var(--seham-text) !important;
}

.message-card:hover,
.users-list .v-list-item:hover,
.user-list-item:hover,
.ip-list-item:hover {
  background: rgba(247, 205, 69, 0.07) !important;
  border-color: rgba(247, 205, 69, 0.38) !important;
}

.system-message,
.broadcast-message {
  background: rgba(247, 205, 69, 0.1) !important;
  border-color: rgba(247, 205, 69, 0.34) !important;
}

.flagged-message .message-card {
  border-left: 4px solid #e45d42 !important;
}

.username,
.message-content,
.users-list .v-list-item-title,
.ip-list .v-list-item-title {
  color: #fff7cf !important;
}

.timestamp,
.typing-text,
.users-list .v-list-item-subtitle,
.ip-list .v-list-item-subtitle,
.empty-state p,
.empty-users p,
.empty-ips p {
  color: rgba(248, 246, 233, 0.62) !important;
}

.users-list .v-list-item,
.ip-list-item {
  border-bottom: 1px solid rgba(247, 205, 69, 0.1) !important;
  color: var(--seham-text) !important;
}

.message-input-section,
.message-input-container,
.input-wrapper,
.typing-indicator-container {
  background: #050504 !important;
  border-top: 1px solid rgba(247, 205, 69, 0.18) !important;
}

.message-input,
.message-input .v-field,
.search-field .v-field {
  background: rgba(255, 255, 255, 0.04) !important;
}

.message-input .v-field,
.search-field .v-field {
  border: 1px solid rgba(247, 205, 69, 0.22) !important;
}

.message-input .v-field--focused,
.search-field .v-field--focused {
  border-color: #f7cd45 !important;
  box-shadow: 0 0 0 2px rgba(247, 205, 69, 0.1) !important;
}

.message-input :deep(input),
.search-field :deep(input) {
  color: #fff7cf !important;
}

.message-input :deep(input::placeholder),
.search-field :deep(input::placeholder) {
  color: rgba(248, 246, 233, 0.56) !important;
}

.send-button,
.v-chip.bg-primary,
.v-chip.bg-success,
.v-chip.bg-warning {
  background: #f7cd45 !important;
  color: #000000 !important;
}

.v-chip.bg-grey {
  background: rgba(248, 246, 233, 0.26) !important;
  color: #ffffff !important;
}

.typing-dots span,
.chat-messages-container::-webkit-scrollbar-thumb,
.users-list::-webkit-scrollbar-thumb {
  background: #f7cd45 !important;
}
</style>
